import { useEffect, useState } from 'react';
import { Col, Row, Card, Statistic, List, Spin, Typography, Tag, Divider, Space, Collapse } from 'antd';
import {
    UserOutlined,
    TeamOutlined,
    CalendarOutlined,
    DollarOutlined,
    SyncOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { AdminService } from '../Services/AdminService';
import {
    WebApiAdminDtoAdminDashboardDto,
    WebApiAdminDtoBatchJobDetailDto,
    WebApiAdminDtoRecentLogDto,
    WebApiAdminDtoRecentSyncRunDto,
    WebApiAdminDtoStaleIntegrationDto
} from '../Repository/eventbookyapi';
import { formatDateString } from '../Helpers/DateHelpers';
import { IntegrationType } from '../Types/IntegrationType';
import { IntegrationState } from '../Types/IntegrationState';
import { JobType } from '../Types/JobType';

const { Panel } = Collapse;

const { Title, Text } = Typography;

const getIntegrationTypeName = (type?: number): string => {
    switch (type) {
        case IntegrationType.Fortnox: return 'Fortnox';
        case IntegrationType.PingPayments: return 'PingPayments';
        case IntegrationType.Unspecified: return 'Unspecified';
        default: return 'Unknown';
    }
};

const getIntegrationStateName = (state?: number): string => {
    switch (state) {
        case IntegrationState.NotCreated: return 'Not Created';
        case IntegrationState.Created: return 'Created';
        case IntegrationState.Pending: return 'Pending';
        case IntegrationState.Active: return 'Active';
        case IntegrationState.Denied: return 'Denied';
        case IntegrationState.Closed: return 'Closed';
        case IntegrationState.Error: return 'Error';
        default: return 'Unknown';
    }
};

const getIntegrationStateColor = (state?: number): string => {
    switch (state) {
        case IntegrationState.Active: return 'success';
        case IntegrationState.Pending: return 'processing';
        case IntegrationState.Created: return 'cyan';
        case IntegrationState.Error: return 'error';
        case IntegrationState.Denied: return 'error';
        case IntegrationState.Closed: return 'default';
        case IntegrationState.NotCreated: return 'default';
        default: return 'default';
    }
};

const getJobTypeName = (jobType?: number): string => {
    switch (jobType) {
        case JobType.SendEmail: return 'Send Email';
        case JobType.CreatePaymentLinks: return 'Create Payment Links';
        case JobType.CreateFortnoxInvoices: return 'Create Fortnox Invoices';
        case JobType.CreateFortnoxCustomers: return 'Create Fortnox Customers';
        case JobType.SendSms: return 'Send SMS';
        case JobType.Unspecified: return 'Unspecified';
        default: return 'Unknown';
    }
};

const AdminDashboard = () => {
    const [dashboardData, setDashboardData] = useState<WebApiAdminDtoAdminDashboardDto | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        try {
            setLoading(true);
            const result = await AdminService.GetDashboard();
            setDashboardData(result);
        } catch (error) {
            console.error('Failed to load dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Row justify="center" align="middle" style={{ minHeight: '400px' }}>
                <Spin size="large" />
            </Row>
        );
    }

    const overview = dashboardData?.systemOverview;
    const activity = dashboardData?.recentActivity;
    const batchJobs = dashboardData?.batchJobsSummary;
    const errors = dashboardData?.errorsSummary;
    const integrations = dashboardData?.integrationStatuses;
    const syncActivity = dashboardData?.syncActivity;

    return (
        <>
            {/* System Overview */}
            <Title level={4}>System Overview</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Card style={{ height: '100%' }}>
                        <Statistic
                            title="Total Companies"
                            value={overview?.totalCompanies ?? 0}
                            prefix={<TeamOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Card style={{ height: '100%' }}>
                        <Statistic
                            title="Total Users"
                            value={overview?.totalUsers ?? 0}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Card style={{ height: '100%' }}>
                        <Statistic
                            title="Total Members"
                            value={overview?.totalMembers ?? 0}
                            prefix={<TeamOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                        <Space>
                            <Text type="secondary">Today: {overview?.newMembersToday ?? 0}</Text>
                            <Text type="secondary">Week: {overview?.newMembersThisWeek ?? 0}</Text>
                        </Space>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Card style={{ height: '100%' }}>
                        <Statistic
                            title="Pending Renewals"
                            value={overview?.companiesExpiringWithin2Months?.length ?? 0}
                            prefix={<ExclamationCircleOutlined />}
                            valueStyle={{ color: overview?.companiesExpiringWithin2Months?.length ? '#faad14' : '#52c41a' }}
                        />
                        <Text type="secondary">Due within 2 months</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Subscription Breakdown" style={{ height: '100%' }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div>
                                <Tag color="magenta">Free</Tag>
                                <Text strong>{overview?.freeCompanies ?? 0}</Text>
                            </div>
                            <div>
                                <Tag color="blue">Basic</Tag>
                                <Text strong>{overview?.basicCompanies ?? 0}</Text>
                            </div>
                            <div>
                                <Tag color="cyan">Basic Plus</Tag>
                                <Text strong>{overview?.basicPlusCompanies ?? 0}</Text>
                            </div>
                            <div>
                                <Tag color="geekblue">Basic Advanced</Tag>
                                <Text strong>{overview?.basicAdvancedCompanies ?? 0}</Text>
                            </div>
                            <div>
                                <Tag color="purple">Premium</Tag>
                                <Text strong>{overview?.premiumCompanies ?? 0}</Text>
                            </div>
                            <div>
                                <Tag color="gold">Enterprise</Tag>
                                <Text strong>{overview?.enterpriseCompanies ?? 0}</Text>
                            </div>
                        </Space>
                    </Card>
                </Col>
            </Row>

            {/* Recent Activity */}
            <Title level={4}>Recent Activity</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} md={8}>
                    <Card>
                        <Statistic
                            title="User Logins Today"
                            value={activity?.userLoginsToday ?? 0}
                            prefix={<UserOutlined />}
                        />
                        <Text type="secondary">This week: {activity?.userLoginsThisWeek ?? 0}</Text>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card>
                        <Statistic
                            title="Fortnox Invoices Imported Today"
                            value={overview?.fortnoxSyncsToday ?? 0}
                            prefix={<SyncOutlined />}
                        />
                        <Text type="secondary">Total invoice rows: {overview?.fortnoxPayments ?? 0}</Text>
                    </Card>
                </Col>
            </Row>

            {/* Integration Sync Activity - is the scheduled Fortnox/Spiris sync actually running? */}
            {syncActivity && (
                <>
                    <Title level={4}>Integration Sync Activity</Title>
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                        {syncActivity.byIntegration?.map((sync) => {
                            // Daily sync + LastRun stamped in server (UTC) time but parsed as local here -
                            // 30h leaves room for both before calling a run missed (matches the API).
                            const isStale = !sync.lastSyncAt || (Date.now() - new Date(sync.lastSyncAt).getTime()) > 30 * 60 * 60 * 1000;
                            return (
                                <Col xs={24} sm={12} md={8} key={sync.integrationName}>
                                    <Card title={sync.integrationName}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <div>
                                                <Text>Last sync: </Text>
                                                {sync.lastSyncAt ? (
                                                    <Text type={isStale ? 'danger' : 'secondary'}>
                                                        {formatDateString(sync.lastSyncAt.toString())}
                                                    </Text>
                                                ) : (
                                                    <Tag color="error">Never</Tag>
                                                )}
                                                {isStale && sync.lastSyncAt && <Tag color="error" style={{ marginLeft: 8 }}>Stale</Tag>}
                                            </div>
                                            <Text>
                                                Runs today: <Text type="success" strong>✓{sync.successfulRunsToday ?? 0}</Text>
                                                {' '}<Text type="danger" strong>✗{sync.failedRunsToday ?? 0}</Text>
                                            </Text>
                                            <Text type="secondary">
                                                Items synced today: {sync.itemsSyncedToday ?? 0} (updated: {sync.itemsUpdatedToday ?? 0})
                                            </Text>
                                            <Text type="secondary">
                                                Companies synced today: {sync.companiesSyncedToday ?? 0} / {sync.activeCompanies ?? 0}
                                            </Text>
                                            <Text type="secondary">
                                                This week: {sync.runsThisWeek ?? 0} runs · {sync.failedRunsThisWeek ?? 0} failed
                                            </Text>
                                        </Space>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>

                    {syncActivity.staleIntegrations && syncActivity.staleIntegrations.length > 0 && (
                        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                            <Col span={24}>
                                <List
                                    header={
                                        <Text strong>
                                            <WarningOutlined style={{ color: '#ff4d4f' }} /> Integrations not synced in the last 24h
                                        </Text>
                                    }
                                    bordered
                                    dataSource={syncActivity.staleIntegrations}
                                    renderItem={(stale: WebApiAdminDtoStaleIntegrationDto) => (
                                        <List.Item>
                                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                                <Space>
                                                    <WarningOutlined style={{ color: '#ff4d4f' }} />
                                                    <div>
                                                        <Text strong>{stale.companyName}</Text>
                                                        <br />
                                                        <Text type="secondary">{stale.integrationName}</Text>
                                                        {stale.state && <Tag color="default" style={{ marginLeft: 8 }}>{stale.state}</Tag>}
                                                    </div>
                                                </Space>
                                                <Space direction="vertical" align="end">
                                                    <Text type="danger">
                                                        {(stale.hoursSinceLastRun ?? -1) < 0 ? 'Never run' : `Last run: ${stale.hoursSinceLastRun}h ago`}
                                                    </Text>
                                                    {stale.lastRunResponse && <Text type="secondary">{stale.lastRunResponse}</Text>}
                                                </Space>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Col>
                        </Row>
                    )}

                    {syncActivity.recentRuns && syncActivity.recentRuns.length > 0 && (
                        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                            <Col span={24}>
                                <Collapse>
                                    <Panel header={`Recent sync runs (${syncActivity.recentRuns.length})`} key="recent-syncs">
                                        <List
                                            dataSource={syncActivity.recentRuns}
                                            renderItem={(run: WebApiAdminDtoRecentSyncRunDto) => (
                                                <List.Item>
                                                    <List.Item.Meta
                                                        title={
                                                            <Space>
                                                                <Tag color={run.success ? 'success' : 'error'}>{run.success ? 'OK' : 'Failed'}</Tag>
                                                                <Text>{run.integrationName} · {run.companyName}</Text>
                                                            </Space>
                                                        }
                                                        description={
                                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                                <Space split="|">
                                                                    {run.createdAt && <Text type="secondary">{formatDateString(run.createdAt.toString())}</Text>}
                                                                    <Text type="secondary">{run.itemCount ?? 0} items</Text>
                                                                    {run.newCount != null && (
                                                                        <Text type="secondary">
                                                                            new: {run.newCount}, updated: {run.updatedCount}, unchanged: {run.unchangedCount}, deleted: {run.deletedCount}
                                                                        </Text>
                                                                    )}
                                                                </Space>
                                                                {run.summary && (
                                                                    <Text type="secondary" style={{ fontSize: '12px' }}>{run.summary}</Text>
                                                                )}
                                                            </Space>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    </Panel>
                                </Collapse>
                            </Col>
                        </Row>
                    )}
                </>
            )}

            {/* Payment Integration Stats */}
            <Title level={4}>Payment Integrations</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} md={8}>
                    <Card>
                        <Statistic
                            title="PingPayment Links Created"
                            value={overview?.pingPaymentLinksCreated ?? 0}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#13c2c2' }}
                        />
                        <Space direction="vertical" style={{ width: '100%', marginTop: '8px' }}>
                            <Text type="secondary">Paid (Total): {overview?.pingPaymentTransactionsPaid ?? 0}</Text>
                            <Text type="secondary">Paid Today: {overview?.pingPaymentTransactionsPaidToday ?? 0}</Text>
                            <Text type="secondary">Settled (Total): {overview?.pingPaymentTransactionsSettled ?? 0}</Text>
                            <Text type="secondary">Settled Today: {overview?.pingPaymentTransactionsSettledToday ?? 0}</Text>
                        </Space>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Batch Jobs Summary">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div>
                                <Text>Today: <Text strong>{batchJobs?.totalJobsToday ?? 0}</Text></Text>
                            </div>
                            <div>
                                <Text>Pending: <Text type="warning">{batchJobs?.pendingJobs ?? 0}</Text></Text>
                            </div>
                            <div>
                                <Text>Processing: <Tag color="processing">{batchJobs?.processingJobs ?? 0}</Tag></Text>
                            </div>
                            <div>
                                <Text>Completed Today: <Tag color="success">{batchJobs?.completedJobsToday ?? 0}</Tag></Text>
                            </div>
                            <div>
                                <Text>Failed Today: <Tag color="error">{batchJobs?.failedJobsToday ?? 0}</Tag></Text>
                            </div>
                        </Space>
                    </Card>
                </Col>

            </Row>

            {/* Expiring Companies Alert */}
            {overview?.companiesExpiringWithin2Months && overview.companiesExpiringWithin2Months.length > 0 && (
                <>
                    <Title level={4}>
                        <ExclamationCircleOutlined style={{ color: '#faad14' }} /> Companies Expiring Within 2 Months
                    </Title>
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                        <Col span={24}>
                            <List
                                bordered
                                dataSource={overview.companiesExpiringWithin2Months}
                                renderItem={(company: any) => (
                                    <List.Item>
                                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                            <Space>
                                                <ClockCircleOutlined style={{ color: '#faad14' }} />
                                                <div>
                                                    <Text strong>{company.companyName}</Text>
                                                    <br />
                                                    <Text type="secondary">{company.subscriptionLevel}</Text>
                                                </div>
                                            </Space>
                                            <Space direction="vertical" align="end">
                                                {company.nextSubscriptionRenewalDate && (
                                                    <Text type="warning">
                                                        Expires: {formatDateString(company.nextSubscriptionRenewalDate.toString())}
                                                    </Text>
                                                )}
                                                <Text type="secondary">{company.totalMembers ?? 0} members</Text>
                                            </Space>
                                        </Space>
                                    </List.Item>
                                )}
                            />
                        </Col>
                    </Row>
                </>
            )}

            {/* Expired Companies */}
            {overview?.companiesExpired && overview.companiesExpired.length > 0 && (
                <>
                    <Title level={4}>
                        <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} /> Expired Companies
                    </Title>
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                        <Col span={24}>
                            <List
                                bordered
                                dataSource={overview.companiesExpired}
                                renderItem={(company: any) => (
                                    <List.Item>
                                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                            <Space>
                                                <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                                                <div>
                                                    <Text strong>{company.companyName}</Text>
                                                    <br />
                                                    <Text type="secondary">{company.subscriptionLevel}</Text>
                                                </div>
                                            </Space>
                                            <Space direction="vertical" align="end">
                                                {company.nextSubscriptionRenewalDate && (
                                                    <Text type="danger">
                                                        Expired: {formatDateString(company.nextSubscriptionRenewalDate.toString())}
                                                    </Text>
                                                )}
                                                {company.daysUntilExpiration !== undefined && (
                                                    <Text type="secondary">{Math.abs(company.daysUntilExpiration)} days ago</Text>
                                                )}
                                                <Text type="secondary">{company.totalMembers ?? 0} members</Text>
                                            </Space>
                                        </Space>
                                    </List.Item>
                                )}
                            />
                        </Col>
                    </Row>
                </>
            )}

            {/* Batch Jobs Details */}
            {(batchJobs?.activeJobs && batchJobs.activeJobs.length > 0) || (batchJobs?.recentFailedJobs && batchJobs.recentFailedJobs.length > 0) ? (
                <>
                    <Title level={4}>Batch Jobs Details</Title>
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                        <Col span={24}>
                            <Collapse>
                                {batchJobs?.activeJobs && batchJobs.activeJobs.length > 0 && (
                                    <Panel header={`Active Jobs (${batchJobs.activeJobs.length})`} key="active">
                                        <List
                                            dataSource={batchJobs.activeJobs}
                                            renderItem={(job: WebApiAdminDtoBatchJobDetailDto) => (
                                                <List.Item>
                                                    <List.Item.Meta
                                                        title={
                                                            <Space>
                                                                <Tag color="processing">{job.status}</Tag>
                                                                <Text>{getJobTypeName(job.jobType as number)}</Text>
                                                            </Space>
                                                        }
                                                        description={
                                                            <Space split="|">
                                                                <Text type="secondary">ID: {job.id}</Text>
                                                                {job.companyName && <Text type="secondary">{job.companyName}</Text>}
                                                                {job.createdAt && (
                                                                    <Text type="secondary">Created: {formatDateString(job.createdAt.toString())}</Text>
                                                                )}
                                                                {job.startedAt && (
                                                                    <Text type="secondary">Started: {formatDateString(job.startedAt.toString())}</Text>
                                                                )}
                                                            </Space>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    </Panel>
                                )}
                                {batchJobs?.recentFailedJobs && batchJobs.recentFailedJobs.length > 0 && (
                                    <Panel header={`Recent Failed Jobs (${batchJobs.recentFailedJobs.length})`} key="failed">
                                        <List
                                            dataSource={batchJobs.recentFailedJobs}
                                            renderItem={(job: WebApiAdminDtoBatchJobDetailDto) => (
                                                <List.Item>
                                                    <List.Item.Meta
                                                        title={
                                                            <Space>
                                                                <Tag color="error">{job.status}</Tag>
                                                                <Text>{getJobTypeName(job.jobType as number)}</Text>
                                                            </Space>
                                                        }
                                                        description={
                                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                                <Space split="|">
                                                                    <Text type="secondary">ID: {job.id}</Text>
                                                                    {job.companyName && <Text type="secondary">{job.companyName}</Text>}
                                                                    {job.createdAt && (
                                                                        <Text type="secondary">Created: {formatDateString(job.createdAt.toString())}</Text>
                                                                    )}
                                                                </Space>
                                                                {job.errorMessage && (
                                                                    <Text type="danger">{job.errorMessage}</Text>
                                                                )}
                                                            </Space>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    </Panel>
                                )}
                            </Collapse>
                        </Col>
                    </Row>
                </>
            ) : null}

            {/* Recent Errors and Warnings */}
            {errors && ((errors.recentErrors && errors.recentErrors.length > 0) || (errors.recentWarnings && errors.recentWarnings.length > 0)) && (
                <>
                    <Title level={4}>Recent Logs</Title>
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                        <Col span={24}>
                            <Collapse>
                                {errors.recentErrors && errors.recentErrors.length > 0 && (
                                    <Panel header={`Recent Errors (${errors.recentErrors.length})`} key="errors">
                                        <List
                                            dataSource={errors.recentErrors}
                                            renderItem={(log: WebApiAdminDtoRecentLogDto) => (
                                                <List.Item>
                                                    <List.Item.Meta
                                                        avatar={<WarningOutlined style={{ color: '#ff4d4f' }} />}
                                                        title={
                                                            <Space>
                                                                <Tag color="error">{log.level}</Tag>
                                                                <Text>{log.message}</Text>
                                                            </Space>
                                                        }
                                                        description={
                                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                                <Text type="secondary">
                                                                    {log.createdAt && formatDateString(log.createdAt.toString())}
                                                                    {log.logger && ` - ${log.logger}`}
                                                                </Text>
                                                                {log.exception && (
                                                                    <Text type="secondary" style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                                                        {log.exception.substring(0, 200)}...
                                                                    </Text>
                                                                )}
                                                            </Space>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    </Panel>
                                )}
                                {errors.recentWarnings && errors.recentWarnings.length > 0 && (
                                    <Panel header={`Recent Warnings (${errors.recentWarnings.length})`} key="warnings">
                                        <List
                                            dataSource={errors.recentWarnings}
                                            renderItem={(log: WebApiAdminDtoRecentLogDto) => (
                                                <List.Item>
                                                    <List.Item.Meta
                                                        avatar={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
                                                        title={
                                                            <Space>
                                                                <Tag color="warning">{log.level}</Tag>
                                                                <Text>{log.message}</Text>
                                                            </Space>
                                                        }
                                                        description={
                                                            <Text type="secondary">
                                                                {log.createdAt && formatDateString(log.createdAt.toString())}
                                                                {log.logger && ` - ${log.logger}`}
                                                            </Text>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    </Panel>
                                )}
                            </Collapse>
                        </Col>
                    </Row>
                </>
            )}

            {/* Errors Summary */}
            {errors && ((errors.errorCountToday ?? 0) > 0 || (errors.warningCountToday ?? 0) > 0) && (
                <>
                    <Title level={4}>Errors & Warnings Summary</Title>
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                        <Col span={24}>
                            <Card>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div>
                                        <Text strong>Today:</Text>
                                    </div>
                                    <div>
                                        <Text>Errors: <Text type="danger" strong>{errors.errorCountToday ?? 0}</Text></Text>
                                    </div>
                                    <div>
                                        <Text>Warnings: <Text type="warning">{errors.warningCountToday ?? 0}</Text></Text>
                                    </div>
                                    <Divider style={{ margin: '12px 0' }} />
                                    <div>
                                        <Text strong>This Week:</Text>
                                    </div>
                                    <div>
                                        <Text>Errors: <Text type="danger">{errors.errorCountThisWeek ?? 0}</Text></Text>
                                    </div>
                                    <div>
                                        <Text>Warnings: <Text type="secondary">{errors.warningCountThisWeek ?? 0}</Text></Text>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}

            {/* Integration Statuses */}
            {integrations && integrations.length > 0 && (
                <>
                    <Title level={4}>Integration Health Status</Title>
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                        <Col span={24}>
                            <List
                                bordered
                                dataSource={integrations}
                                renderItem={(integration) => (
                                    <List.Item>
                                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                            <Space>
                                                {integration.isHealthy ? (
                                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                ) : (
                                                    <WarningOutlined style={{ color: '#ff4d4f' }} />
                                                )}
                                                <div>
                                                    <Text strong>{integration.companyName}</Text>
                                                    <br />
                                                    <Text type="secondary">{getIntegrationTypeName(integration.type as number)}</Text>
                                                    {integration.state !== undefined && (
                                                        <Tag color={getIntegrationStateColor(integration.state as number)}>
                                                            {getIntegrationStateName(integration.state as number)}
                                                        </Tag>
                                                    )}
                                                </div>
                                            </Space>
                                            <Space direction="vertical" align="end">
                                                {integration.lastRun && (
                                                    <Text type="secondary">
                                                        Last run: {formatDateString(integration.lastRun.toString())}
                                                    </Text>
                                                )}
                                                {integration.healthMessage && (
                                                    <Tag color={integration.isHealthy ? "success" : "error"}>{integration.healthMessage}</Tag>
                                                )}
                                                {integration.isTokenExpiringSoon && (
                                                    <Tag color="warning">Token expiring soon</Tag>
                                                )}
                                            </Space>
                                        </Space>
                                    </List.Item>
                                )}
                            />
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
};

export default AdminDashboard;
